import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { KpiCard, KpiGrid } from '@/shared/components/dashboard/KpiCard';
import { Icons } from '@/shared/components/ui/icons';
import type { PlatformUsageLiveMetrics } from '../utils/liveMetrics';

interface PlatformUsageOverviewProps {
  metrics: PlatformUsageLiveMetrics;
  lastUpdatedAt: string | null;
  locale: string;
}

export function PlatformUsageOverview({
  metrics,
  lastUpdatedAt,
  locale,
}: PlatformUsageOverviewProps) {
  const { t } = useTranslation();

  const lastUpdateLabel = lastUpdatedAt
    ? new Date(lastUpdatedAt).toLocaleString(locale)
    : t('platformUsage.overview.lastUpdatePending');

  return (
    <KpiGrid>
      <KpiCard
        title={t('platformUsage.overview.activeSessions')}
        value={String(metrics.activeSessions)}
        tone="blue"
        icon={<Icons.BarChart className="h-5 w-5" aria-hidden />}
      />
      <KpiCard
        title={t('platformUsage.overview.activeUsers')}
        value={String(metrics.activeUsers)}
        tone="green"
        icon={<Icons.Users className="h-5 w-5" aria-hidden />}
      />
      <KpiCard
        title={t('platformUsage.overview.activeTenants')}
        value={String(metrics.activeTenants)}
        tone="violet"
        icon={<Icons.Building className="h-5 w-5" aria-hidden />}
      />
      <KpiCard
        title={t('platformUsage.overview.lastUpdate')}
        value={lastUpdateLabel}
        tone="cyan"
        icon={<Icons.RefreshCw className="h-5 w-5" aria-hidden />}
      />
    </KpiGrid>
  );
}

export function PlatformUsageOverviewSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {Array.from({ length: 4 }).map((_, index) => (
        <div key={index} className="rounded-xl border border-border bg-card p-5 animate-pulse">
          <div className="h-3 w-28 rounded bg-muted" />
          <div className="mt-3 h-8 w-16 rounded bg-muted" />
        </div>
      ))}
    </div>
  );
}

export function useSortedSessions<T extends { lastOccurredAt: string }>(sessions: T[]): T[] {
  return useMemo(
    () =>
      [...sessions].sort(
        (a, b) => new Date(b.lastOccurredAt).getTime() - new Date(a.lastOccurredAt).getTime(),
      ),
    [sessions],
  );
}
